import ConstantsImages from "../constants/ConstantsImages";
import ConstantsTexts from "../constants/ConstantsTexts";
import ConstantsParams from "../constants/ConstantsParams";
import ConstantsPages from "../constants/ConstantsPages";
import ConstantsStrings from "../constants/ConstantsStrings";
import awsmobile from "../aws-exports";

class UtilsUnit {
  static regexDecimalOneNumber = /^\d*\.?\d{0,1}$/;
  static regexDecimalTwoNumber = /^\d*\.?\d{0,2}$/;
  static regexInt = /^\d*$/;

  static getGaugeImageSrc(number) {
    let imageSrc;

    switch (number) {
      case 1:
        imageSrc = ConstantsImages.GAUGE_1_IMAGE;
        break;
      case 2:
        imageSrc = ConstantsImages.GAUGE_2_IMAGE;
        break;
      case 3:
        imageSrc = ConstantsImages.GAUGE_3_IMAGE;
        break;
      case 4:
        imageSrc = ConstantsImages.GAUGE_4_IMAGE;
        break;
      case 5:
        imageSrc = ConstantsImages.GAUGE_5_IMAGE;
        break;
      default:
        imageSrc = ConstantsImages.GAUGE_5_IMAGE;
    }

    return imageSrc;
  }

  static getGaugeNumberBackgroundColor(number) {
    let backgroundColor;

    switch (number) {
      case 1:
        backgroundColor = "#EB6366";
        break;
      case 2:
        backgroundColor = "#CD8CFF";
        break;
      case 3:
        backgroundColor = "#5971F6";
        break;
      case 4:
        backgroundColor = "#65A2D7";
        break;
      case 5:
        backgroundColor = "#70C2AE";
        break;
      default:
        backgroundColor = "#70C2AE";
    }

    return backgroundColor;
  }

  static getGaugeNumberTextColor(number) {
    let textColor;

    switch (number) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
        textColor = "#FFF";
        break;
      default:
        textColor = "#FFF";
    }

    return textColor;
  }

  static getGaugeNumberText(number) {
    const numeric = Number(number);
    if (!Number.isFinite(numeric)) {
      return "";
    }

    return numeric < 10 ? `0${numeric}` : `${numeric}`;
  }

  static formatYearOfNeedLabel(value) {
    const str = String(value ?? "").trim();
    if (!str) {
      return "";
    }

    if (str.includes("שנת")) {
      return str;
    }

    const untilYearMatch = str.match(/^עד\s*(\d{4})$/);
    if (untilYearMatch) {
      return `עד שנת ${untilYearMatch[1]}`;
    }

    if (/^\d{4}$/.test(str)) {
      return `עד שנת ${str}`;
    }

    return str;
  }

  // Converts a percentage to a lighter grayscale color (0-100 to hex)
  static getLighterColor(number) {
    const clamped = Math.max(1, Math.min(100, number));
    const value = Math.floor(((100 - clamped) / 100) * 127);
    const hex = value.toString(16).padStart(2, "0");
    return `#${hex}${hex}${hex}`;
  }

  // Formats numbers with comma separators for thousands
  static formatNumberWithCommas(value) {
    if (this.isValueUndefinedOrNull(value)) {
      return "";
    }

    const cleanedValue = value.toString().replace(/[^0-9.]/g, "");
    const [integerPart, decimalPart] = cleanedValue.split(".");
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart !== undefined ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
  }

  static remToPx(rem) {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * fontSize;
  }

  static removeCommas(value) {
    return value.replace(/,/g, "");
  }

  // Formats numbers with Israeli Shekel symbol and locale formatting
  static formatNumberWithShekel(num) {
    if (this.isValueUndefinedOrNull(num)) {
      return "₪0";
    }
    return `₪${num.toLocaleString()}`;
  }

  // Formats numbers with percentage symbol and locale formatting
  static formatNumberWithPercent(num) {
    if (this.isValueUndefinedOrNull(num)) {
      return "0%";
    }
    return `${num.toLocaleString()}%`;
  }

  // Combines shekel formatting with comma separators
  static formatNumberWithCommaWithShekel(num) {
    return this.formatNumberWithShekel(this.formatNumberWithCommas(num));
  }

  static removePercentageSign(text) {
    return text.replace("%", "");
  }

  static toNumberWithoutTrailingZeros(num) {
    return parseFloat(num);
  }

  // Finds a value in a list by matching serial key and returns the corresponding value key
  static getValueFromList(serial, list, serialKey, valueKey) {
    if (!list || !Array.isArray(list)) {
      return "";
    }

    const measure = list.find((measure) => measure[serialKey] === serial);
    return measure ? measure[valueKey] : "";
  }

  static isValueUndefinedOrNull(value) {
    return value === undefined || value == null;
  }

  // Returns default center coordinates for map (Tel Aviv area)
  static getCenterPropForMap() {
    return { lat: 32.0853, lng: 34.7818 };
  }

  static generateNumberArray(min, max) {
    const result = [];

    for (let i = min; i <= max; i++) {
      result.push({ number: i.toString() });
    }

    return result;
  }

  // Formats date string to DD.MM.YYYY format
  static formatDate(inputDateString) {
    const date = new Date(inputDateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // Formats date string to HH:MM format
  static formatDateHour(inputDateString) {
    const date = new Date(inputDateString);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  // Compares two JSON objects by normalizing values to strings
  static areJSONsEqualLoose(json1, json2) {
    return JSON.stringify(this.normalizeValues(json1)) === JSON.stringify(this.normalizeValues(json2));
  }

  // Normalizes object values by converting numbers to strings for comparison
  static normalizeValues(obj) {
    return JSON.parse(JSON.stringify(obj, (key, value) => (typeof value === "number" ? String(value) : value)));
  }

  static getAccessToken() {
    return sessionStorage.getItem(ConstantsStrings.ACCESS_TOKEN);
  }

  static getRefreshToken() {
    return sessionStorage.getItem(ConstantsStrings.REFRESH_TOKEN);
  }

  static getLogoutInProgress() {
    return sessionStorage.getItem(ConstantsStrings.LOGOUT_IN_PROGRESS);
  }

  static handleSetTokens({ access_token, refresh_token }) {
    if (access_token) {
      sessionStorage.setItem(ConstantsStrings.ACCESS_TOKEN, access_token);
    }
    if (refresh_token) {
      sessionStorage.setItem(ConstantsStrings.REFRESH_TOKEN, refresh_token);
    }
  }

  static handleSetLogoutInProgress() {
    sessionStorage.setItem(ConstantsStrings.LOGOUT_IN_PROGRESS, "true");
  }

  static handleRemoveTokens() {
    sessionStorage.removeItem(ConstantsStrings.ACCESS_TOKEN);
    sessionStorage.removeItem(ConstantsStrings.REFRESH_TOKEN);
  }

  static handleRemoveLogoutInProgress() {
    sessionStorage.removeItem(ConstantsStrings.LOGOUT_IN_PROGRESS);
  }

  static handleClearLoginTimestamp(dispatch) {
    dispatch({ type: ConstantsStrings.SET_LOGIN_TIMESTAMP, payload: null });
  }

  static handleSetLoginTimestamp(dispatch) {
    dispatch({ type: ConstantsStrings.SET_LOGIN_TIMESTAMP, payload: Date.now() });
  }

  // Checks if login has expired (8 hours from login timestamp)
  static isLoginExpired(loginTimestamp) {
    if (!loginTimestamp) return false;

    const currentTime = Date.now();
    const eightHoursInMs = 8 * 60 * 60 * 1000; // 8 hours
    return currentTime - loginTimestamp > eightHoursInMs;
  }

  static handleSetUserData(dispatch, userData) {
    dispatch({ type: ConstantsStrings.SET_USER_DATA, payload: userData });
  }

  static getUserID(userData) {
    return userData.Username;
  }

  // Combines given name and family name from user attributes
  static getUserName(userData) {
    const userAttributes = this.getUserAttributes(userData) || [];
    const givenName = userAttributes.find((attr) => attr.Name === "given_name")?.Value;
    const familyName = userAttributes.find((attr) => attr.Name === "family_name")?.Value;
    const fullName = `${givenName || ""} ${familyName || ""}`.trim();
    if (fullName) {
      return fullName;
    }

    const email = userAttributes.find((attr) => attr.Name === "email")?.Value;
    if (email) {
      return email.split("@")[0];
    }

    return userData?.Username || "";
  }

  static getUserAttributes(userData) {
    return userData.UserAttributes;
  }

  // Extracts and parses user type terminal value from user attributes
  static getUserTypeTerminal(userData) {
    const value = this.getUserAttributes(userData)?.find((attr) => attr.Name === "custom:UserTypeTerminal")?.Value;
    return value !== undefined ? parseInt(value) : undefined;
  }

  // Checks if user has valid UserTypeTerminal (not NONE and attribute exists)
  static hasValidUserTypeTerminal(userData) {
    const userTypeTerminal = this.getUserTypeTerminal(userData);
    return userTypeTerminal !== undefined && userTypeTerminal > ConstantsParams.UserTypeTerminalStatus.NONE;
  }

  static editorUser(userData) {
    return this.getUserTypeTerminal(userData) === ConstantsParams.UserTypeTerminalStatus.EDITOR;
  }

  static editorUserAbove(userData) {
    return this.getUserTypeTerminal(userData) >= ConstantsParams.UserTypeTerminalStatus.EDITOR;
  }

  static managerUser(userData) {
    return this.getUserTypeTerminal(userData) === ConstantsParams.UserTypeTerminalStatus.MANAGER;
  }

  static handleSearchTerminalClick(navigate) {
    navigate(ConstantsPages.PAGE_FILTER);
  }

  // Navigates to model info page (only when not in edit mode)
  static handleModelFrameClick(navigate, editMode = false) {
    if (editMode) return;

    navigate(ConstantsPages.PAGE_MODEL_INFO);
  }

  // Navigates to dashboard page (only when not in edit mode)
  static handleBusFrameClick(navigate, editMode = false) {
    if (editMode) return;

    navigate(ConstantsPages.PAGE_DASHBOARD);
  }

  // Navigates to terminal info page with terminal data (only when not in edit mode)
  static handleSearchFrameClick(navigate, selectedTerminal, editMode = false) {
    if (editMode) return;

    if (selectedTerminal) {
      this.handleNavigateToTerminalInfo(navigate, false, selectedTerminal["terminalID"]);
    } else {
      alert(ConstantsTexts.CODE_110);
    }
  }

  // Navigates to terminal info page in create new terminal mode (only when not in edit mode)
  static handleCreateTerminalClick(navigate, editMode = false) {
    if (editMode) return;

    this.handleNavigateToTerminalInfo(navigate, true, null);
  }

  // Navigates to terminal info page with terminal data (only when not in edit mode)
  static handleNavigateToTerminalInfo(navigate, createNewTerminalMode, terminalID, changeRequest = null) {
    navigate(ConstantsPages.PAGE_TERMINAL_INFO, {
      state: {
        createNewTerminalMode: createNewTerminalMode,
        terminalID: terminalID,
        changeRequest: changeRequest,
        timestamp: Date.now(),
      },
    });
  }

  static checkResponseStatus(errorMessage) {
    alert(errorMessage);
    throw new Error("Network response was not ok");
  }

  static isUserAuthenticated(userData) {
    const hasUserData = userData && Object.keys(userData).length > 0;
    const hasAccessToken = this.getAccessToken();
    const hasValidUserType = this.hasValidUserTypeTerminal(userData);
    return hasUserData && hasAccessToken && hasValidUserType;
  }

  static clickOutside() {
    document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
  }

  static handleLogout(dispatch) {
    this.handleRemoveTokens();
    this.handleClearLoginTimestamp(dispatch);
    this.logout();
  }

  static getCognitoDomain() {
    let domain = awsmobile?.Auth?.Cognito?.loginWith?.oauth?.domain || "";
    domain = String(domain).trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
    const oauthPath = domain.search(/\/oauth2(\/|$)/);
    if (oauthPath !== -1) {
      domain = domain.slice(0, oauthPath);
    }
    return domain;
  }

  // Initiates Cognito OAuth login flow with configured parameters
  static login() {
    const cognitoMap = awsmobile.Auth.Cognito;
    const oauthMap = cognitoMap.loginWith.oauth;
    const cognitoDomain = this.getCognitoDomain();
    const responseType = oauthMap.responseType;
    const clientId = cognitoMap.userPoolClientId;
    const redirectUri = window.location.hostname === "localhost" ? oauthMap.redirectSignIn[1] : oauthMap.redirectSignIn[0];
    const scope = oauthMap.scopes.join(" ");

    const queryParams = new URLSearchParams({
      response_type: responseType,
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: scope,
    }).toString();

    const loginUrl = `https://${cognitoDomain}/oauth2/authorize?${queryParams}`;

    window.location.assign(loginUrl);
  }

  // Initiates Cognito OAuth logout flow with configured parameters
  static logout() {
    this.handleSetLogoutInProgress();

    const cognitoMap = awsmobile.Auth.Cognito;
    const oauthMap = cognitoMap.loginWith.oauth;
    const cognitoDomain = this.getCognitoDomain();
    const clientId = cognitoMap.userPoolClientId;
    const redirectUri = window.location.hostname === "localhost" ? oauthMap.redirectSignIn[1] : oauthMap.redirectSignIn[0];

    const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri)}`;

    window.location.assign(logoutUrl);
  }
}

export default UtilsUnit;
